import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "../../App";
import {
  Users, Edit, Trash2, Mail,
  Phone, X, Save, MessageSquare,
  Shield, ShieldCheck, ShieldAlert, AlertTriangle, Building2, History
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { FilterBar } from "@/components/admin/FilterBar";
import { MultiSelectFilter } from "@/components/admin/MultiSelectFilter";
import { useUrlFilters } from "@/hooks/useUrlFilters";
import { useFilterOptions } from "@/hooks/useFilterOptions";

const LEAD_TYPES = [
  { value: "renter", label: "Renter" },
  { value: "buyer", label: "Buyer" },
  { value: "investor", label: "Investor" },
];
const LEAD_STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "touring", label: "Touring" },
  { value: "converted", label: "Converted" },
  { value: "lost", label: "Lost" },
];

const Leads = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [timeline, setTimeline] = useState([]);
  const { options: enums } = useFilterOptions();

  // Global filter state — URL synced + session persistent
  const FILTER_CONFIG = useMemo(() => ({
    q: { type: "string" },
    lead_type: { type: "multi" },
    status: { type: "multi" },
    source: { type: "multi" },
    partner_type: { type: "multi" },
    company_id: { type: "multi" },
    assigned_agent: { type: "multi" },
    engagement_tier: { type: "multi" },
    created_from: { type: "string" },
    created_to: { type: "string" },
  }), []);
  const { filters, setFilter, reset, toParams, activeCount } = useUrlFilters(
    "leads", FILTER_CONFIG,
  );

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` },
        params: toParams(),
      });
      setLeads(response.data);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    } finally {
      setLoading(false);
    }
  }, [token, toParams]);

  useEffect(() => {
    const t = setTimeout(fetchLeads, 250);
    return () => clearTimeout(t);
  }, [fetchLeads]);

  // Deep-link support: /admin/leads?lead=<id> opens that lead's detail modal.
  useEffect(() => {
    const leadId = searchParams.get("lead");
    if (!leadId || selectedLead) return;
    const match = leads.find((l) => l.id === leadId);
    if (match) {
      setSelectedLead(match);
    } else if (!loading) {
      axios.get(`${API}/leads/${leadId}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setSelectedLead(r.data))
        .catch(() => {});
    }
     
  }, [leads, loading, searchParams, selectedLead, token]);

  // Corporate CRM lead timeline (lead_events) for the open lead
  useEffect(() => {
    if (!selectedLead?.id) { setTimeline([]); return; }
    axios.get(`${API}/partners/leads/${selectedLead.id}/timeline`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((r) => setTimeline(r.data.events || []))
      .catch(() => setTimeline([]));
     
  }, [selectedLead?.id, token]);

  const buildEditData = (lead) => ({
    name: lead.name || "",
    email: lead.email || "",
    phone: lead.phone || "",
    lead_type: lead.lead_type || "renter",
    status: lead.status || "new",
    budget_min: lead.budget_min ?? "",
    budget_max: lead.budget_max ?? "",
    move_in_date: lead.move_in_date || "",
    bedrooms: lead.bedrooms || "",
    assigned_agent: lead.assigned_agent || "",
    preferred_areas: (lead.preferred_areas || []).join(", "),
    notes: lead.notes || "",
  });

  const updateLead = async () => {
    const payload = {
      name: editData.name?.trim() || undefined,
      email: editData.email?.trim() || undefined,
      phone: editData.phone?.trim() || null,
      lead_type: editData.lead_type,
      status: editData.status,
      budget_min: editData.budget_min !== "" ? parseInt(editData.budget_min, 10) : null,
      budget_max: editData.budget_max !== "" ? parseInt(editData.budget_max, 10) : null,
      move_in_date: editData.move_in_date || null,
      bedrooms: editData.bedrooms?.trim() || null,
      assigned_agent: editData.assigned_agent?.trim() || null,
      preferred_areas: (editData.preferred_areas || "").split(",").map((s) => s.trim()).filter(Boolean),
      notes: editData.notes || "",
    };
    try {
      const res = await axios.put(`${API}/leads/${selectedLead.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Lead updated successfully" });
      fetchLeads();
      setEditMode(false);
      setSelectedLead(res.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast({
        title: "Failed to update lead",
        description: typeof detail === "string" ? detail : "Please check the fields and try again",
        variant: "destructive",
      });
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Lead deleted" });
      setSelectedLead(null);
      fetchLeads();
    } catch {
      toast({ title: "Failed to delete lead", variant: "destructive" });
    }
  };

  // CSV export — always reflects the currently-active filters.
  const csvUrl = useMemo(() => {
    const params = new URLSearchParams(toParams()).toString();
    return `${API}/leads/export/csv${params ? `?${params}` : ""}`;
  }, [toParams]);

  const filteredLeads = leads.filter((lead) => {
    const q = (filters.q || "").toLowerCase();
    if (!q) return true;
    return (lead.name || "").toLowerCase().includes(q) ||
           (lead.email || "").toLowerCase().includes(q);
  });

  const getStatusColor = (status) => {
    const colors = {
      new: "bg-yellow-100 text-yellow-800",
      contacted: "bg-blue-100 text-blue-800",
      touring: "bg-orange-100 text-orange-800",
      converted: "bg-green-100 text-green-800",
      lost: "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getTypeColor = (type) => {
    const colors = {
      renter: "bg-blue-100 text-blue-800",
      buyer: "bg-green-100 text-green-800",
      investor: "bg-purple-100 text-purple-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  const getAttribution = (lead) => {
    if (lead.agent_registered_confirmed) return { label: "Confirmed", color: "bg-green-100 text-green-700", icon: ShieldCheck };
    if (lead.instruction_acknowledged) return { label: "Protected", color: "bg-green-50 text-green-600", icon: Shield };
    if (["touring", "applied"].includes(lead.stage)) return { label: "At Risk", color: "bg-red-100 text-red-700", icon: ShieldAlert };
    if ((lead.tour_intent_score || 0) >= 5) return { label: "Needs Reminder", color: "bg-amber-100 text-amber-700", icon: AlertTriangle };
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0A2342] font-['Playfair_Display']" data-testid="leads-title">
            Leads
          </h1>
          <p className="text-slate-600">Manage and track your leads</p>
        </div>
      </div>

      {/* Filters — global multi-select, URL synced */}
      <FilterBar
        searchValue={filters.q}
        onSearchChange={(v) => setFilter("q", v)}
        searchPlaceholder="Search by name, email, or phone…"
        onReset={reset}
        activeCount={activeCount}
        csvUrl={csvUrl}
        csvFilename="leads_export.csv"
        testId="leads-filters"
      >
        <MultiSelectFilter
          label="Type"
          options={LEAD_TYPES}
          value={filters.lead_type || []}
          onChange={(v) => setFilter("lead_type", v)}
          testId="leads-filter-type"
        />
        <MultiSelectFilter
          label="Status"
          options={LEAD_STATUSES}
          value={filters.status || []}
          onChange={(v) => setFilter("status", v)}
          testId="leads-filter-status"
        />
        <MultiSelectFilter
          label="Source"
          options={enums?.lead_sources || []}
          value={filters.source || []}
          onChange={(v) => setFilter("source", v)}
          testId="leads-filter-source"
        />
        <MultiSelectFilter
          label="Engagement"
          options={(enums?.engagement_tiers || []).map((t) => ({ value: t, label: t.toUpperCase() }))}
          value={filters.engagement_tier || []}
          onChange={(v) => setFilter("engagement_tier", v)}
          testId="leads-filter-engagement"
        />
        <MultiSelectFilter
          label="Partner Type"
          options={enums?.partner_types || []}
          value={filters.partner_type || []}
          onChange={(v) => setFilter("partner_type", v)}
          testId="leads-filter-partner-type"
        />
        <MultiSelectFilter
          label="Company"
          options={(enums?.companies || []).map((c) => ({ value: c.id, label: c.name }))}
          value={filters.company_id || []}
          onChange={(v) => setFilter("company_id", v)}
          testId="leads-filter-company"
        />
        <MultiSelectFilter
          label="Agent"
          options={enums?.assigned_agents || []}
          value={filters.assigned_agent || []}
          onChange={(v) => setFilter("assigned_agent", v)}
          testId="leads-filter-agent"
        />
        <div className="flex items-center gap-1" data-testid="leads-filter-date">
          <span className="text-xs text-slate-500">Created</span>
          <input
            type="date"
            value={filters.created_from || ""}
            onChange={(e) => setFilter("created_from", e.target.value)}
            className="h-9 px-2 text-sm border border-slate-300 rounded-md"
            data-testid="leads-filter-created-from"
          />
          <span className="text-xs text-slate-400">–</span>
          <input
            type="date"
            value={filters.created_to || ""}
            onChange={(e) => setFilter("created_to", e.target.value)}
            className="h-9 px-2 text-sm border border-slate-300 rounded-md"
            data-testid="leads-filter-created-to"
          />
        </div>
      </FilterBar>

      {/* Leads Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2342]"></div>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No leads found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Name</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Type</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Score</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600 hidden md:table-cell">Attribution</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Source</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Date</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr
                    key={lead.id}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => {
                      setSelectedLead(lead);
                      setEditData(buildEditData(lead));
                    }}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-[#0A2342]">{lead.name}</p>
                        <p className="text-sm text-slate-500">{lead.email}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`badge ${getTypeColor(lead.lead_type)}`}>
                        {lead.lead_type}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`badge ${getStatusColor(lead.status)}`}>
                        {lead.status}
                      </span>
                    </td>
                    <td className="p-4">
                      {(() => {
                        const score = lead.engagement_score ?? 0;
                        const tier = lead.engagement_tier || "cold";
                        const cls = tier === "hot"
                          ? "bg-red-100 text-red-700 border-red-300"
                          : tier === "warm"
                          ? "bg-amber-100 text-amber-700 border-amber-300"
                          : "bg-slate-100 text-slate-600 border-slate-200";
                        return (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${cls}`}
                            title={`${tier.toUpperCase()} — ${tier === "hot" ? "Call first" : tier === "warm" ? "Active drip" : "Passive drip"}`}
                            data-testid={`lead-score-${lead.id}`}
                          >
                            {score}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      {(() => {
                        const attr = getAttribution(lead);
                        if (!attr) return <span className="text-xs text-slate-400">—</span>;
                        const Icon = attr.icon;
                        return (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${attr.color}`} data-testid={`lead-attribution-${lead.id}`}>
                            <Icon className="w-3 h-3" /> {attr.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="p-4 text-slate-600 text-sm">{lead.source}</td>
                    <td className="p-4 text-slate-600 text-sm">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteLead(lead.id);
                        }}
                        className="text-red-500 hover:text-red-700 p-2"
                        data-testid={`lead-delete-${lead.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="lead-detail-modal"
          >
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0A2342] font-['Playfair_Display']">
                Lead Details
              </h2>
              <button
                onClick={() => {
                  setSelectedLead(null);
                  setEditMode(false);
                }}
                className="text-slate-400 hover:text-slate-600"
                data-testid="lead-detail-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div>
                <h3 className="text-lg font-semibold text-[#0A2342] mb-3">{selectedLead.name}</h3>
                <div className="space-y-2">
                  <div className="flex items-center text-slate-600">
                    <Mail className="w-4 h-4 mr-2" />
                    <a href={`mailto:${selectedLead.email}`} className="hover:text-[#DE6E4B]">
                      {selectedLead.email}
                    </a>
                  </div>
                  {selectedLead.phone && (
                    <div className="flex items-center text-slate-600">
                      <Phone className="w-4 h-4 mr-2" />
                      <a href={`tel:${selectedLead.phone}`} className="hover:text-[#DE6E4B]">
                        {selectedLead.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                <span className={`badge ${getTypeColor(selectedLead.lead_type)}`}>
                  {selectedLead.lead_type}
                </span>
                <span className={`badge ${getStatusColor(selectedLead.status)}`}>
                  {selectedLead.status}
                </span>
                {(() => {
                  const attr = getAttribution(selectedLead);
                  if (!attr) return null;
                  const Icon = attr.icon;
                  return (
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${attr.color}`}>
                      <Icon className="w-3 h-3" /> {attr.label}
                    </span>
                  );
                })()}
              </div>

              {/* CLP Info */}
              {(selectedLead.tour_intent_score > 0 || selectedLead.stage !== "new" || selectedLead.favorites?.length > 0) && (
                <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-sm">
                  <p className="text-xs text-slate-500 font-medium mb-1.5">Commission Lock</p>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-slate-600">Intent Score: <strong className="text-[#0A2342]">{selectedLead.tour_intent_score || 0}</strong></span>
                    <span className="text-slate-600">Stage: <strong className="text-[#0A2342] capitalize">{selectedLead.stage || "new"}</strong></span>
                    <span className="text-slate-600">Views: <strong className="text-[#0A2342]">{selectedLead.property_views || 0}</strong></span>
                  </div>
                  {selectedLead.favorites?.length > 0 && (
                    <p className="text-xs text-slate-500">{selectedLead.favorites.length} favorited properties</p>
                  )}
                </div>
              )}

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                {selectedLead.budget_min && (
                  <div>
                    <span className="text-slate-500">Budget:</span>
                    <span className="ml-2 text-[#0A2342] font-medium">
                      ${selectedLead.budget_min} - ${selectedLead.budget_max}
                    </span>
                  </div>
                )}
                {selectedLead.bedrooms && (
                  <div>
                    <span className="text-slate-500">Bedrooms:</span>
                    <span className="ml-2 text-[#0A2342] font-medium">{selectedLead.bedrooms}</span>
                  </div>
                )}
                {selectedLead.move_in_date && (
                  <div>
                    <span className="text-slate-500">Move-in:</span>
                    <span className="ml-2 text-[#0A2342] font-medium">{selectedLead.move_in_date}</span>
                  </div>
                )}
                {selectedLead.assigned_agent && (
                  <div>
                    <span className="text-slate-500">Agent:</span>
                    <span className="ml-2 text-[#0A2342] font-medium" data-testid="lead-detail-agent">{selectedLead.assigned_agent}</span>
                  </div>
                )}
                {selectedLead.preferred_areas?.length > 0 && (
                  <div className="col-span-2">
                    <span className="text-slate-500">Areas:</span>
                    <span className="ml-2 text-[#0A2342] font-medium">
                      {selectedLead.preferred_areas.join(", ")}
                    </span>
                  </div>
                )}
              </div>

              {/* Message */}
              {selectedLead.message && (
                <div>
                  <span className="text-slate-500 text-sm">Message:</span>
                  <p className="mt-1 text-[#0A2342] bg-slate-50 p-3 rounded-lg">{selectedLead.message}</p>
                </div>
              )}

              {/* Corporate CRM Timeline (lead_events) */}
              {timeline.length > 0 && (
                <div data-testid="lead-timeline-section">
                  <span className="text-slate-500 text-sm flex items-center mb-2">
                    <History className="w-4 h-4 mr-1" /> Timeline
                    {selectedLead.company_id && (
                      <span className="ml-2 inline-flex items-center gap-1 text-xs bg-orange-50 text-[#DE6E4B] px-2 py-0.5 rounded-full font-medium">
                        <Building2 className="w-3 h-3" /> Corporate Partner Lead
                      </span>
                    )}
                  </span>
                  <ol className="relative border-l border-slate-200 ml-2 space-y-3">
                    {timeline.map((ev) => (
                      <li key={ev.id} className="ml-4" data-testid="lead-timeline-event">
                        <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-[#DE6E4B]" />
                        <div className="text-sm text-[#0A2342] font-medium">{ev.summary || ev.event_type}</div>
                        <div className="text-xs text-slate-400">
                          {ev.event_at ? new Date(ev.event_at).toLocaleString() : ""}
                          {ev.actor ? ` · ${ev.actor}` : ""}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Full Edit Form */}
              {editMode ? (
                <div className="space-y-4 border-t border-slate-200 pt-4" data-testid="lead-edit-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Name</label>
                      <input
                        type="text"
                        value={editData.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        className="input-field"
                        data-testid="lead-edit-name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
                      <input
                        type="email"
                        value={editData.email}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        className="input-field"
                        data-testid="lead-edit-email"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Phone</label>
                      <input
                        type="tel"
                        value={editData.phone}
                        onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                        className="input-field"
                        placeholder="(555) 555-5555"
                        data-testid="lead-edit-phone"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Type</label>
                      <select
                        value={editData.lead_type}
                        onChange={(e) => setEditData({ ...editData, lead_type: e.target.value })}
                        className="input-field"
                        data-testid="lead-edit-type"
                      >
                        {LEAD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Status</label>
                      <select
                        value={editData.status}
                        onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                        className="input-field"
                        data-testid="lead-edit-status"
                      >
                        {LEAD_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Assigned Agent</label>
                      <input
                        type="text"
                        value={editData.assigned_agent}
                        onChange={(e) => setEditData({ ...editData, assigned_agent: e.target.value })}
                        className="input-field"
                        placeholder="Agent name"
                        data-testid="lead-edit-agent"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Budget Min ($)</label>
                      <input
                        type="number"
                        value={editData.budget_min}
                        onChange={(e) => setEditData({ ...editData, budget_min: e.target.value })}
                        className="input-field"
                        placeholder="1000"
                        data-testid="lead-edit-budget-min"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Budget Max ($)</label>
                      <input
                        type="number"
                        value={editData.budget_max}
                        onChange={(e) => setEditData({ ...editData, budget_max: e.target.value })}
                        className="input-field"
                        placeholder="2000"
                        data-testid="lead-edit-budget-max"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Move-in Date</label>
                      <input
                        type="date"
                        value={editData.move_in_date}
                        onChange={(e) => setEditData({ ...editData, move_in_date: e.target.value })}
                        className="input-field"
                        data-testid="lead-edit-move-date"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Bedrooms</label>
                      <input
                        type="text"
                        value={editData.bedrooms}
                        onChange={(e) => setEditData({ ...editData, bedrooms: e.target.value })}
                        className="input-field"
                        placeholder="e.g. 2 Bed"
                        data-testid="lead-edit-bedrooms"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Preferred Areas (comma-separated)</label>
                      <input
                        type="text"
                        value={editData.preferred_areas}
                        onChange={(e) => setEditData({ ...editData, preferred_areas: e.target.value })}
                        className="input-field"
                        placeholder="Midtown, Heights, Montrose"
                        data-testid="lead-edit-areas"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
                    <textarea
                      value={editData.notes}
                      onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                      className="textarea-field"
                      rows={3}
                      placeholder="Add notes about this lead..."
                      data-testid="lead-edit-notes"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={updateLead} className="btn-primary flex items-center" data-testid="lead-save">
                      <Save className="w-4 h-4 mr-2" /> Save
                    </button>
                    <button
                      onClick={() => setEditMode(false)}
                      className="btn-secondary"
                      data-testid="lead-cancel-edit"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="border-t border-slate-200 pt-4">
                  {selectedLead.notes && (
                    <div className="mb-4">
                      <span className="text-slate-500 text-sm flex items-center mb-1">
                        <MessageSquare className="w-4 h-4 mr-1" /> Notes
                      </span>
                      <p className="text-[#0A2342] bg-slate-50 p-3 rounded-lg">{selectedLead.notes}</p>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setEditData(buildEditData(selectedLead));
                      setEditMode(true);
                    }}
                    className="btn-primary flex items-center"
                    data-testid="lead-edit-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" /> Update Lead
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Leads;
